// Loads equirectangular panoramas as PlayCanvas textures via the engine's pc.Asset
// pipeline (handles flipY + sRGB correctly).
//
// • Caches by URL so re-entering a room is instant after the first visit.
// • On failure, returns a procedural gradient texture so the app never crashes
//   from a missing JPG.

import * as pc from 'playcanvas';
import { log } from '../app/logger';

export interface AssetLoadResult {
  texture: pc.Texture;
  isFallback: boolean;
}

export class AssetLoader {
  private readonly app: pc.Application;
  private readonly cache = new Map<string, pc.Texture>();
  private readonly inFlight = new Map<string, Promise<AssetLoadResult>>();
  private fallbackTex: pc.Texture | null = null;

  constructor(app: pc.Application) {
    this.app = app;
  }

  loadPanorama(url: string, label?: string): Promise<AssetLoadResult> {
    const cached = this.cache.get(url);
    if (cached) return Promise.resolve({ texture: cached, isFallback: false });

    const inFlight = this.inFlight.get(url);
    if (inFlight) return inFlight;

    const promise = this.loadFresh(url, label).finally(() => this.inFlight.delete(url));
    this.inFlight.set(url, promise);
    return promise;
  }

  preload(urls: readonly string[]): Promise<void> {
    return Promise.allSettled(urls.map((u) => this.loadPanorama(u))).then(() => undefined);
  }

  /** Procedural fallback — used when a panorama URL fails to load. */
  getFallback(label = 'Panorama indisponível'): pc.Texture {
    if (this.fallbackTex) return this.fallbackTex;
    this.fallbackTex = buildProceduralGradient(this.app.graphicsDevice, label);
    return this.fallbackTex;
  }

  /** Hard ceiling on a single panorama load. A pc.Asset 'error' fires on an
   *  HTTP failure, but a *hung* request (stalled CDN, flaky mobile/Quest Wi-Fi)
   *  never errors — without this the load Promise would never settle and the
   *  loading screen would spin forever. On timeout we resolve the procedural
   *  fallback so the tour stays usable. */
  private static readonly LOAD_TIMEOUT_MS = 12_000;

  private loadFresh(url: string, label?: string): Promise<AssetLoadResult> {
    return new Promise((resolve) => {
      let settled = false;
      const done = (result: AssetLoadResult): void => {
        if (settled) return;
        settled = true;
        clearTimeout(timer);
        resolve(result);
      };
      const timer = setTimeout(() => {
        log.event('asset_load_failed', { url, reason: 'timeout', ms: AssetLoader.LOAD_TIMEOUT_MS });
        done({ texture: this.getFallback(label ?? url), isFallback: true });
      }, AssetLoader.LOAD_TIMEOUT_MS);

      const asset = new pc.Asset(`pano:${url}`, 'texture', { url });
      asset.once('load', () => {
        const tex = asset.resource as pc.Texture;
        // Equirect: U wraps (seamless), V clamps at the poles to avoid artefacts.
        tex.addressU = pc.ADDRESS_REPEAT;
        tex.addressV = pc.ADDRESS_CLAMP_TO_EDGE;
        tex.minFilter = pc.FILTER_LINEAR_MIPMAP_LINEAR;
        tex.magFilter = pc.FILTER_LINEAR;
        const dev = this.app.graphicsDevice;
        if (dev && dev.maxAnisotropy) {
          tex.anisotropy = Math.min(16, dev.maxAnisotropy);
        }
        this.cache.set(url, tex);
        done({ texture: tex, isFallback: false });
      });
      asset.once('error', (err: unknown) => {
        log.event('asset_load_failed', { url, reason: 'error', detail: String(err) });
        done({ texture: this.getFallback(label ?? url), isFallback: true });
      });
      this.app.assets.add(asset);
      this.app.assets.load(asset);
    });
  }

  destroy(): void {
    for (const tex of this.cache.values()) tex.destroy();
    this.cache.clear();
    this.fallbackTex?.destroy();
    this.fallbackTex = null;
  }
}

/**
 * Build a 1024×512 gradient panorama with the given label baked in. Used when an
 * expected panorama JPG is missing.
 */
function buildProceduralGradient(device: pc.GraphicsDevice, label: string): pc.Texture {
  const w = 1024;
  const h = 512;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2d context unavailable');

  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, '#1a2238');
  grad.addColorStop(0.5, '#3a4a6a');
  grad.addColorStop(1, '#1a1208');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);

  ctx.fillStyle = 'rgba(255,255,255,0.92)';
  ctx.font = '600 32px ui-sans-serif, system-ui, sans-serif';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText('Imersivo 3D', w / 2, h / 2 - 22);
  ctx.font = '400 18px ui-sans-serif, system-ui, sans-serif';
  ctx.fillStyle = 'rgba(255,255,255,0.65)';
  ctx.fillText(label, w / 2, h / 2 + 14);

  const tex = new pc.Texture(device, {
    format: pc.PIXELFORMAT_RGB8,
    addressU: pc.ADDRESS_REPEAT,
    addressV: pc.ADDRESS_CLAMP_TO_EDGE,
    minFilter: pc.FILTER_LINEAR_MIPMAP_LINEAR,
    magFilter: pc.FILTER_LINEAR,
    anisotropy: 8,
    mipmaps: true,
  });
  tex.setSource(canvas);
  return tex;
}
